import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldIconButton } from "@emerald-react/icon-button";
import { EmeraldInput } from "@emerald-react/input";
import { EmeraldButtonSize, EmeraldButtonVariant } from "@emerald/nxcore";
import { useUpdatePropertyMutation } from "@services/survey-properties";
import { Property } from "@survey-types/survey.property.types";
import { selectPropertiesWithOrder } from "@survey/surveySelectors";
import {
  currentPropertyId as currentPropertyAction,
  updateProperty as updatePropertyAction,
} from "@survey/surveySlice";
import React, { useEffect, useState } from "react";

interface PropertyDetailHeaderFormProps {
  property: Property;
  propertyId: string;
}

const PropertyDetailHeaderForm = ({
  property,
  propertyId,
}: PropertyDetailHeaderFormProps) => {
  const dispatch = useAppDispatch();
  const [propertyName, setPropertyName] = useState(property?.name || "");
  const [updateProperty] = useUpdatePropertyMutation();

  useEffect(() => {
    setPropertyName(property?.name || "");
  }, [property?.name]);

  // All properties in list order (hidden ones excluded from navigation)
  const allProperties = useAppSelector(selectPropertiesWithOrder).filter(
    (p) => !p.isHidden,
  );
  const currentIndex = allProperties.findIndex((p) => p.id === propertyId);
  const total = allProperties.length;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < total - 1;

  const navigateTo = (index: number) => {
    const target = allProperties[index];
    if (target) {
      dispatch(currentPropertyAction({ propertyId: target.id }));
    }
  };

  const showPropertiesList = () => {
    dispatch(currentPropertyAction({ propertyId: null }));
  };

  const handlePropertyNameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPropertyName(event.target.value);
  };

  const handlePropertyNameBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = event.target.value.trim();
    if (propertyId && value !== property?.name) {
      dispatch(
        updatePropertyAction({
          propertyId: propertyId,
          updates: { name: value },
        }),
      );
      updateProperty({
        propertyId: propertyId,
        updates: { name: value },
      });
    }
  };

  return (
    <div className="Property-details-header flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-x-1 items-center">
          <EmeraldIconButton
            icon="keyboard_arrow_left"
            onClick={showPropertiesList}
            type="Icon"
          />
          <EmeraldInput
            value={propertyName || "Untitled Property"}
            className="w-64 emerald-standard propertyName"
            inputSize="small"
            outlined={false}
            onChange={handlePropertyNameChange}
            onBlur={handlePropertyNameBlur}
          />
        </div>

        <div className="flex items-center gap-3">
          {/* Property record navigator */}
          {total > 1 && (
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 bg-white"
              title="Navigate between properties"
            >
              <button
                type="button"
                onClick={() => navigateTo(currentIndex - 1)}
                disabled={!hasPrev}
                aria-label="Previous property"
                className="btn-nav-primary group flex items-center justify-center w-7 h-7 rounded-full border border-primary text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-primary"
              >
                <EmeraldIcon
                  icon="chevron_left"
                  variant="icon"
                  fontsizeInRem={1.1}
                  className="text-primary group-hover:text-white!"
                />
              </button>
              <span className="text-sm text-slate-500 min-w-14 text-center select-none">
                {currentIndex + 1} of {total}
              </span>
              <button
                type="button"
                onClick={() => navigateTo(currentIndex + 1)}
                disabled={!hasNext}
                aria-label="Next property"
                className="btn-nav-primary group flex items-center justify-center w-7 h-7 rounded-full border border-primary text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-primary"
              >
                <EmeraldIcon
                  icon="chevron_right"
                  variant="icon"
                  fontsizeInRem={1.1}
                  className="text-primary group-hover:text-white!"
                />
              </button>
            </div>
          )}

          <EmeraldButton
            label="See All Properties"
            type={EmeraldButtonVariant.TextButton}
            size={EmeraldButtonSize.Medium}
            nativeProps={{
              title: "See all properties",
              type: "button",
              "aria-labelledby": "see-all-properties",
              "aria-label": "See all properties",
            }}
            onClick={showPropertiesList}
          />
        </div>
      </div>
      <div className="warning-message text-data bg-info-alert border-info-icon border flex p-2.5 gap-2 text-sm items-center rounded-sm">
        <EmeraldIcon
          icon="info"
          type="filled"
          variant="icon"
          className="text-info-icon"
        />
        <span>
          Edits made to property related fields below will display in this
          survey only. Edits will not update the Site IQ project properties or
          Site IQ team data.
        </span>
      </div>
    </div>
  );
};

export default PropertyDetailHeaderForm;
